import React, {useEffect, useState} from "react";
import {useAuthInfo} from "@propelauth/react";

import {getBulkInviteScopes, ScopeChoice, selectBulkInviteScopes} from "../../api/bulk_invite_api";
import {Button} from "../ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "../ui/card";
import {Checkbox} from "../ui/checkbox";
import {Input} from "../ui/input";
import {Spinner} from "../ui/spinner";
import {useToast} from "../ui/use-toast";

interface SelectScopesProps {
    token: string;
    projectLine: string;
    developerCompany: string;
}

/**
 * Step 2 of the bulk invite: pick which scopes you want to bid on.
 *
 * Only scopes the developer marked `open` are offered. Scopes the user already
 * has a permission for come back as selected and are locked - selection is
 * additive, there is no un-select anywhere in the app.
 */
export function SelectScopes({token, projectLine, developerCompany}: SelectScopesProps) {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [scopes, setScopes] = useState<ScopeChoice[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");

    useEffect(() => {
        if (!accessToken) return;
        getBulkInviteScopes(token, accessToken)
            .then((data) => {
                setScopes(data);
                setSelected(data.filter(s => s.selected).map(s => s.id));
                setIsLoading(false);
            })
            .catch(() => {
                setScopes([]);
                setIsLoading(false);
            });
    }, [token, accessToken]);

    const toggle = (scope: ScopeChoice) => {
        if (scope.selected) return; // already granted, cannot be removed here
        setSelected((prev) =>
            prev.includes(scope.id)
                ? prev.filter(id => id !== scope.id)
                : prev.concat(scope.id)
        );
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
        selectBulkInviteScopes(token, selected, accessToken!)
            .then((data) => {
                // full page load, NOT navigate(). the sidebar and breadcrumb read
                // their project list (and scopes_enabled) off the whoami payload,
                // which App.tsx only fetches on a login change - a client side
                // navigate would leave the nav showing the state from before we
                // granted any permissions, so the Scopes tab would be missing.
                window.location.replace(data.project_id ? `/project/${data.project_id}` : '/');
            })
            .catch((e) => {
                setIsSubmitting(false);
                toast({
                    title: "Error",
                    description: e.message || "Could not save your selection.",
                    variant: "destructive",
                });
            });
    };

    if (isLoading) return <Spinner/>;

    // nothing on offer - they still have project level access from the claim
    if (scopes.length === 0) {
        return <Card>
            <CardHeader>
                <CardTitle className="font-normal !text-[17px]">You're on {projectLine}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                    There are no scopes open for selection yet. {developerCompany} will be in touch.
                </p>
                {/* full page load so whoami refetches - the claim just gave them a project */}
                <Button onClick={() => window.location.replace('/')}>Go to my projects</Button>
            </CardContent>
        </Card>;
    }

    // filtering only changes what is shown - `selected` is keyed off scope id,
    // so anything ticked stays ticked when the query changes
    const q = query.trim().toLowerCase();
    const visible = q ? scopes.filter(s => s.title.toLowerCase().includes(q)) : scopes;

    const newlyPicked = selected.filter(id => !scopes.find(s => s.id === id)?.selected).length;

    return <Card>
        <CardHeader>
            <CardTitle className="font-normal !text-[17px]">
                What do you want to bid on?
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
                Select the scopes you want to price on {projectLine}. You can ask
                {' '}{developerCompany} for more later.
            </p>

            <Input
                placeholder="Search scopes..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />

            {/* capped height so the Continue button stays on screen - projects
                can have 40+ scopes, each with a paragraph of description */}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto rounded-md border p-2">
                {visible.length === 0 &&
                    <div className="text-sm text-gray-500 p-2">
                        No scopes match "{query}".
                    </div>}
                {visible.map((scope) => (
                    <div
                        key={scope.id}
                        className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent"
                        onClick={() => toggle(scope)}
                    >
                        <Checkbox
                            className="mt-1"
                            checked={selected.includes(scope.id)}
                            disabled={scope.selected}
                            onCheckedChange={() => toggle(scope)}
                        />
                        <div className="flex-1">
                            <div className="text-sm font-medium">
                                {scope.number !== null && scope.number !== undefined ? `#${scope.number} - ` : ''}{scope.title}
                            </div>
                            {scope.description &&
                                <div className="text-xs text-gray-500 mt-1">{scope.description}</div>}
                            {scope.selected &&
                                <div className="text-xs text-gray-400 mt-1">Already added</div>}
                        </div>
                    </div>
                ))}
            </div>

            <Button
                onClick={handleSubmit}
                disabled={isSubmitting || newlyPicked === 0}
                className="w-full"
            >
                {isSubmitting ? 'Saving...' : `Continue${newlyPicked > 0 ? ` with ${newlyPicked} scope${newlyPicked > 1 ? 's' : ''}` : ''}`}
            </Button>
        </CardContent>
    </Card>;
}

export default SelectScopes;
